import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Descriptions, Space, Divider, Typography, Avatar } from 'antd';
import { UserOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { userAPI } from '../services/api';
import { getUser, setUser } from '../utils/auth';

const { Title } = Typography;

const ProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [form] = Form.useForm();

  // 获取用户信息
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getProfile();
      // 后端返回的数据格式是 {user: teacher, stats: stats}
      const userData = response.data.user || response.data;
      setProfileData(userData);
      form.setFieldsValue(userData);
    } catch (error: any) {
      message.error('获取用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 保存用户信息
  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await userAPI.updateProfile(values);
      
      // 更新本地存储的用户信息
      const currentUser = getUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...values };
        setUser(updatedUser);
      }
      
      setProfileData({ ...profileData, ...values });
      setEditing(false);
      message.success('个人资料更新成功');
    } catch (error: any) {
      message.error(error.response?.data?.error || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消编辑
  const handleCancel = () => {
    form.setFieldsValue(profileData);
    setEditing(false);
  };

  if (!profileData) {
    return <Card loading={true} />;
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 头部信息 */}
          <div style={{ textAlign: 'center' }}>
            <Avatar size={80} icon={<UserOutlined />} style={{ marginBottom: 16 }} />
            <Title level={3}>{profileData.name || '用户'}</Title>
            <p style={{ color: '#666' }}>@{profileData.username}</p>
          </div>

          <Divider />

          {/* 个人信息表单 */}
          {editing ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={profileData}
            >
              <Form.Item
                label="姓名"
                name="name"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>

              <Form.Item
                label="邮箱"
                name="email"
                rules={[
                  { type: 'email', message: '请输入有效的邮箱地址' },
                ]}
              >
                <Input placeholder="请输入邮箱" />
              </Form.Item>

              <Form.Item
                label="电话"
                name="phone"
                rules={[
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' },
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>

              <Form.Item
                label="学校"
                name="school"
              >
                <Input placeholder="请输入学校名称" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    icon={<SaveOutlined />}
                    loading={loading}
                  >
                    保存
                  </Button>
                  <Button 
                    onClick={handleCancel}
                    icon={<CloseOutlined />}
                  >
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <>
              <Descriptions 
                title="基本信息" 
                bordered
                extra={
                  <Button 
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setEditing(true)}
                  >
                    编辑资料
                  </Button>
                }
              >
                <Descriptions.Item label="用户名">
                  {profileData.username}
                </Descriptions.Item>
                <Descriptions.Item label="姓名">
                  {profileData.name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="角色">
                  教师
                </Descriptions.Item>
                <Descriptions.Item label="邮箱">
                  {profileData.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="电话">
                  {profileData.phone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="学校">
                  {profileData.school || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="注册时间">
                  {profileData.created_at ? new Date(profileData.created_at).toLocaleString('zh-CN') : '-'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* 账户安全 */}
              <Card title="账户安全" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>登录密码</strong>
                      <p style={{ margin: 0, color: '#666', fontSize: 12 }}>
                        建议定期更换密码以保护账户安全
                      </p>
                    </div>
                    <Button type="link">修改密码</Button>
                  </div>
                </Space>
              </Card>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default ProfilePage;